import type { CloudVault } from '../vault/cloudVault';
import { pendingText, queueSnapshot } from '../vault/writeQueue';
import { pathsOfKind } from '../vault/kinds';
import type { KindVault } from '../vault/kinds';
import { parseWorkspace } from './workspace';
import type { Workspace } from './workspace';
import { isDeskContainer } from '../desk/deskContainer';

type NookVault = KindVault & Pick<CloudVault, 'readFiles'>;

/**
 * Every nook — a handful of small files, read whole. A write this tab still
 * owes wins over the vault's copy, as on the board.
 */
export async function loadWorkspaces(vault: NookVault): Promise<Workspace[]> {
  const files = await vault.readFiles(await pathsOfKind(vault, 'workspace'));
  const byPath = new Map(files.map((f) => [f.path, f.text]));
  for (const [path, text] of queueSnapshot().pending) {
    if (byPath.has(path)) byPath.set(path, text);
  }
  return [...byPath].flatMap(([path, text]) => (text ? (parseWorkspace(path, text) ?? []) : []));
}

/**
 * The desk's container where the nooks are read: the one nook file that is the container
 * ([isDeskContainer]), wherever an older build moved it, as the vault holds it. [loadWorkspaces]
 * never reads it as a nook.
 */
export async function findDeskContainer(vault: NookVault): Promise<{ path: string; text: string } | null> {
  const files = await vault.readFiles(await pathsOfKind(vault, 'workspace'));
  return files.find((f) => isDeskContainer(f.text)) ?? null;
}

/** [workspaces] as their files stand now: one this tab still owes a write to, as that write has it. */
export function asTheyStand(workspaces: readonly Workspace[]): Workspace[] {
  return workspaces.flatMap((w) => {
    const text = pendingText(w.path);
    return text === undefined ? [w] : (parseWorkspace(w.path, text) ?? []);
  });
}
